import React, { useMemo, useRef, useState } from 'react';
import { Box, Button, ButtonBase, IconButton, Popover, Typography } from '@mui/material';
import { ChevronLeft, ChevronRight, KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { buildCalendarDayStatusMap, getCalendarDayStatus } from '../Timeline/utils/calendarDayStatus';
import { getCalendarDateKey } from '../../utils/calendarDateKey';
import TimelineDayStatusMarkers from './TimelineDayStatusMarkers';
import TimelineDateCalendar from './TimelineDateCalendar';
import colors from '../../assets/theme/colors';
import { alpha } from '@mui/material/styles';

const DAY_WINDOW = 7;
const SWIPE_THRESHOLD_PX = 48;
const SWIPE_AXIS_RATIO = 1.2;

const getSafeDate = (value) => {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) {
    return new Date();
  }

  return value;
};

const toStartOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

const buildRelativeDays = (selectedDate, windowSize = DAY_WINDOW) => {
  const today = toStartOfDay(new Date());
  const selectedDay = toStartOfDay(getSafeDate(selectedDate));
  const centeredDate = selectedDay > today ? today : selectedDay;
  const before = Math.floor(windowSize / 2);
  const days = [];

  for (let offset = -before; offset <= before; offset += 1) {
    const date = new Date(centeredDate);
    date.setDate(centeredDate.getDate() + offset);
    days.push(date);
  }

  return days;
};

const TimelineDateNavigator = ({
  selectedDate,
  onDateChange,
  onOpenCalendar,
  entries = [],
  activityDateKeys = [],
  dayWindow = DAY_WINDOW,
  inlineCalendar = false,
  showJumpToday = false,
  accent = null,
  sx = {},
}) => {
  const activeAccent = accent || {
    primary: colors.semantic.sageAction,
    ink: colors.semantic.sageActionInk,
    soft: colors.semantic.sageMist,
  };
  const [calendarAnchor, setCalendarAnchor] = useState(null);
  const swipeStateRef = useRef({
    tracking: false,
    startX: 0,
    startY: 0,
    swiping: false,
  });
  const ignoreNextDayClickRef = useRef(false);
  const resolvedSelectedDate = getSafeDate(selectedDate);
  const dayStatusMap = useMemo(
    () => buildCalendarDayStatusMap(entries, activityDateKeys),
    [activityDateKeys, entries]
  );
  const stripDays = useMemo(
    () => buildRelativeDays(resolvedSelectedDate, dayWindow),
    [dayWindow, resolvedSelectedDate]
  );
  const today = useMemo(() => new Date(), []);
  const todayOnly = toStartOfDay(today);
  const selectedKey = getCalendarDateKey(resolvedSelectedDate);
  const nextDate = useMemo(() => {
    const candidate = new Date(resolvedSelectedDate);
    candidate.setDate(candidate.getDate() + 1);
    return candidate;
  }, [resolvedSelectedDate]);
  const isNextDisabled = toStartOfDay(nextDate) > todayOnly;
  const isCalendarOpen = Boolean(calendarAnchor);

  const handleCalendarOpen = (event) => {
    event.stopPropagation();
    if (inlineCalendar) {
      setCalendarAnchor(event.currentTarget);
      return;
    }
    onOpenCalendar?.(event);
  };

  const handleCalendarClose = () => {
    setCalendarAnchor(null);
  };

  const handleDateChange = (date) => {
    onDateChange?.(date);
    handleCalendarClose();
  };

  const handleJumpToday = () => {
    handleDateChange(new Date());
  };

  const moveByDays = (delta) => {
    const nextDate = new Date(resolvedSelectedDate);
    nextDate.setDate(nextDate.getDate() + delta);

    if (toStartOfDay(nextDate) > todayOnly) {
      return;
    }

    onDateChange?.(nextDate);
  };

  const getSwipePoint = (event) => {
    const touch = event.touches?.[0] || event.changedTouches?.[0];
    if (touch) {
      return {
        x: touch.clientX,
        y: touch.clientY,
      };
    }

    return {
      x: event.clientX,
      y: event.clientY,
    };
  };

  const startSwipeTracking = (event) => {
    const point = getSwipePoint(event);
    if (typeof point.x !== 'number' || typeof point.y !== 'number') {
      return;
    }

    swipeStateRef.current = {
      tracking: true,
      startX: point.x,
      startY: point.y,
      swiping: false,
    };
  };

  const updateSwipeTracking = (event) => {
    const swipeState = swipeStateRef.current;
    if (!swipeState.tracking) {
      return;
    }

    const point = getSwipePoint(event);
    if (typeof point.x !== 'number' || typeof point.y !== 'number') {
      return;
    }

    const deltaX = point.x - swipeState.startX;
    const deltaY = point.y - swipeState.startY;
    if (Math.abs(deltaX) > 20 && Math.abs(deltaX) > Math.abs(deltaY) * SWIPE_AXIS_RATIO) {
      swipeStateRef.current = {
        ...swipeState,
        swiping: true,
      };
    }
  };

  const endSwipeTracking = (event) => {
    const swipeState = swipeStateRef.current;
    if (!swipeState.tracking) {
      return;
    }

    swipeStateRef.current = {
      ...swipeState,
      tracking: false,
    };

    const point = getSwipePoint(event);
    if (typeof point.x !== 'number' || typeof point.y !== 'number') {
      return;
    }

    const deltaX = point.x - swipeState.startX;
    const deltaY = point.y - swipeState.startY;
    const isHorizontalSwipe = Math.abs(deltaX) >= SWIPE_THRESHOLD_PX
      && Math.abs(deltaX) > Math.abs(deltaY) * SWIPE_AXIS_RATIO;
    if (!isHorizontalSwipe) {
      return;
    }

    ignoreNextDayClickRef.current = true;
    moveByDays(deltaX < 0 ? 1 : -1);
    window.setTimeout(() => {
      ignoreNextDayClickRef.current = false;
    }, 0);
  };

  const handleStripPointerDown = (event) => {
    if (event.button !== undefined && event.button !== 0) {
      return;
    }

    startSwipeTracking(event);
  };

  const handleDayClick = (date) => {
    if (ignoreNextDayClickRef.current) {
      return;
    }

    onDateChange?.(date);
  };

  const formatDateLabel = (date) => (
    date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  );

  return (
    <Box sx={{ width: '100%', ...sx }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'auto minmax(0, 1fr) auto',
          alignItems: 'center',
          gap: 1,
          minWidth: 0,
        }}
      >
        <IconButton
          size="small"
          onClick={() => moveByDays(-1)}
          aria-label="Previous day"
          sx={{
            width: { xs: 42, sm: 46 },
            height: { xs: 42, sm: 46 },
            border: '1px solid',
            borderColor: colors.landing.borderLight,
            bgcolor: colors.landing.surface,
            flexShrink: 0,
            color: colors.landing.heroText,
            boxShadow: `0 4px 12px ${colors.landing.shadowSoft}`,
          }}
        >
          <ChevronLeft sx={{ fontSize: 24 }} />
        </IconButton>

        <ButtonBase
          onClick={(event) => {
            handleCalendarOpen(event);
          }}
          aria-label="Open calendar"
          sx={{
            minWidth: 0,
            px: 0.6,
            py: 0.45,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.25,
            color: 'text.primary',
            fontWeight: 800,
            letterSpacing: 0,
            textAlign: 'center',
            '&:hover': {
              bgcolor: 'action.hover',
            },
          }}
        >
          <Typography
            component="span"
            sx={{
              fontSize: { xs: '1.04rem', sm: '1.16rem' },
              fontWeight: 850,
              letterSpacing: 0,
              lineHeight: 1.1,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {formatDateLabel(resolvedSelectedDate)}
          </Typography>
          {isCalendarOpen ? (
            <KeyboardArrowUp sx={{ fontSize: 18, flexShrink: 0, color: colors.landing.textMuted }} />
          ) : (
            <KeyboardArrowDown sx={{ fontSize: 18, flexShrink: 0, color: colors.landing.textMuted }} />
          )}
        </ButtonBase>

        <IconButton
          size="small"
          onClick={() => moveByDays(1)}
          aria-label="Next day"
          disabled={isNextDisabled}
          sx={{
            width: { xs: 42, sm: 46 },
            height: { xs: 42, sm: 46 },
            border: '1px solid',
            borderColor: colors.landing.borderLight,
            bgcolor: colors.landing.surface,
            flexShrink: 0,
            color: colors.landing.heroText,
            boxShadow: `0 4px 12px ${colors.landing.shadowSoft}`,
          }}
        >
          <ChevronRight sx={{ fontSize: 24 }} />
        </IconButton>
      </Box>

      <Box
        onPointerDown={handleStripPointerDown}
        onPointerMove={updateSwipeTracking}
        onPointerUp={endSwipeTracking}
        onPointerCancel={endSwipeTracking}
        onTouchStart={startSwipeTracking}
        onTouchMove={updateSwipeTracking}
        onTouchEnd={endSwipeTracking}
        onTouchCancel={endSwipeTracking}
        sx={{
          mt: 1.05,
          display: 'grid',
          gridTemplateColumns: `repeat(${stripDays.length}, minmax(0, 1fr))`,
          gap: { xs: 0.7, sm: 0.9 },
          width: '100%',
          touchAction: 'pan-y',
          userSelect: 'none',
        }}
      >
        {stripDays.map((date) => {
          const dateKey = getCalendarDateKey(date);
          const status = dateKey ? getCalendarDayStatus(dayStatusMap.get(dateKey) || {}) : getCalendarDayStatus();
          const isSelected = dateKey === selectedKey;
          const isFutureDay = toStartOfDay(date) > todayOnly;
          const weekdayLabel = date.toLocaleDateString('en-US', { weekday: 'short' });

          return (
            <ButtonBase
              key={dateKey}
              onClick={() => handleDayClick(date)}
              disabled={isFutureDay}
              aria-label={date.toLocaleDateString('en-US', {
                weekday: 'long',
                month: 'long',
                day: 'numeric',
                year: 'numeric',
              })}
              sx={{
                minWidth: 0,
                px: 0.4,
                py: { xs: 0.8, sm: 0.9 },
                minHeight: { xs: 70, sm: 78 },
                borderRadius: '18px',
                border: '1px solid',
                borderColor: isSelected ? 'transparent' : colors.landing.borderLight,
                bgcolor: isSelected ? activeAccent.primary : colors.landing.surface,
                color: isSelected ? activeAccent.ink : colors.landing.heroText,
                opacity: isFutureDay ? 0.45 : 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.15,
                boxShadow: isSelected ? `0 10px 22px ${alpha(activeAccent.primary, 0.2)}` : 'none',
                transition: 'transform 0.18s ease, box-shadow 0.18s ease, background-color 0.18s ease',
                '&:hover': {
                  transform: 'translateY(-1px)',
                  bgcolor: isSelected ? activeAccent.primary : activeAccent.soft,
                },
                '&.Mui-disabled': {
                  color: colors.landing.textMuted,
                  cursor: 'default',
                  pointerEvents: 'none',
                },
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: '0.74rem', sm: '0.78rem' },
                  fontWeight: 750,
                  letterSpacing: 0,
                  lineHeight: 1,
                  opacity: isSelected ? 0.95 : 0.74,
                }}
              >
                {weekdayLabel}
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: '1rem', sm: '1.05rem' },
                  fontWeight: 700,
                  lineHeight: 1.1,
                  letterSpacing: 0,
                }}
              >
                {date.getDate()}
              </Typography>
              <TimelineDayStatusMarkers
                hasLogs={status.hasLogs}
                hasIncident={status.hasIncident}
                selected={isSelected}
                size="sm"
                sx={{ mt: 0.15 }}
              />
            </ButtonBase>
          );
        })}
      </Box>

      {inlineCalendar ? (
        <Popover
          open={isCalendarOpen}
          anchorEl={calendarAnchor}
          onClose={handleCalendarClose}
          disableScrollLock
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          transformOrigin={{ vertical: 'top', horizontal: 'center' }}
          PaperProps={{
            sx: {
              mt: 1.25,
              width: 'min(88vw, 340px)',
              borderRadius: '26px',
              border: `1px solid ${colors.landing.borderLight}`,
              boxShadow: `0 24px 70px ${colors.landing.shadowPanel}`,
              overflow: 'hidden',
              bgcolor: colors.landing.surface,
            },
          }}
        >
          <Box sx={{ px: 1.2, pt: 1.2, pb: 1.35 }}>
            <TimelineDateCalendar
              value={resolvedSelectedDate}
              onChange={(date) => date && handleDateChange(date)}
              entries={entries}
              activityDateKeys={activityDateKeys}
              sx={{
                '& .MuiPickersCalendarHeader-label': {
                  color: colors.landing.heroText,
                  fontWeight: 850,
                },
                '& .MuiPickersCalendarHeader-switchViewButton': {
                  display: 'none',
                },
              }}
            />
            {showJumpToday ? (
              <Button
                fullWidth
                variant="contained"
                onClick={handleJumpToday}
                sx={{
                  mt: 0.5,
                  minHeight: 46,
                  borderRadius: '16px',
                  textTransform: 'none',
                  fontWeight: 850,
                  color: colors.app.dashboard.quickAction.primaryGradientStart,
                  bgcolor: colors.landing.sageLight,
                  boxShadow: 'none',
                  '&:hover': {
                    bgcolor: colors.app.dashboard.quickAction.secondaryHoverBg,
                    boxShadow: 'none',
                  },
                }}
              >
                Jump to today
              </Button>
            ) : null}
          </Box>
        </Popover>
      ) : null}
    </Box>
  );
};

export default TimelineDateNavigator;
